import { useRef, useState } from "react";
import { api, type Booking, type Client } from "../../lib/api";
import { Modal } from "../ui/Modal";



// ─── ShootDoneModal ───────────────────────────────────────────────────────────

export function ShootDoneModal({ booking, clients, onClose, onDone }: {
  booking: Booking;
  clients: Client[];
  onClose: () => void;
  onDone: () => void;
}) {
  const client = clients.find(c => c.ID === booking.ClientID);
  const clientName = client?.DisplayName ?? "";
  const today = new Date();
  const twoWeeks = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const defaultMsg = client?.Email
    ? `Hi ${clientName}!\n\nGreat session — thanks for today! Your photos will be ready by ${twoWeeks}. I'll send you the link as soon as the gallery is live.\n\nBest,`
    : "";

  const [deliveryDate, setDeliveryDate] = useState(twoWeeks);
  const [message, setMessage] = useState(defaultMsg);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ email_sent: boolean; gallery_slug: string } | null>(null);
  // keep onDone stable across renders
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.markShootDone(booking.ID, { delivery_date: deliveryDate, message });
      setResult({ email_sent: r.email_sent, gallery_slug: r.gallery_slug });
      onDoneRef.current(); // refresh the booking list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setSending(false);
    }
  }

  const lineItemsSummary = booking.LineItems && booking.LineItems.length > 0
    ? booking.LineItems.map(li => li.Name).join(", ")
    : null;

  return (
    <Modal open onClose={onClose} title="Shoot done!" maxWidth={520} zIndex={300}>
        {result ? (
          <div>
            <div style={{ display: "grid", gap: ".5rem", marginBottom: "1rem" }}>
              <div>&#10003; Booking moved to Processing</div>
              {result.email_sent && <div>&#9993; Notification sent to client</div>}
              {result.gallery_slug && <div>&#128247; Empty gallery created for upload</div>}
            </div>
            {result.gallery_slug && (
              <a href={`#/galleries/${result.gallery_slug}`} onClick={onClose} className="btn-primary"
                 style={{ display: "block", textAlign: "center", textDecoration: "none" }}>
                Open gallery for upload &#8594;
              </a>
            )}
            <button onClick={onClose} className="btn-ghost" style={{ width: "100%", marginTop: ".5rem" }}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSend} style={{ display: "grid", gap: "1rem" }}>
            {lineItemsSummary && (
              <div style={{ background: "var(--bg)", borderRadius: 8, padding: "0.6rem 0.75rem", fontSize: "0.85rem", color: "var(--muted)" }}>
                This booking includes: {lineItemsSummary}
              </div>
            )}
            <div className="form-group" style={{ margin: 0 }}>
              <label>Expected delivery date *</label>
              <input type="date" required value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} />
            </div>
            {client?.Email && (
              <div className="form-group" style={{ margin: 0 }}>
                <label>Message to {clientName} ({client.Email})</label>
                <textarea rows={6} value={message} onChange={e => setMessage(e.target.value)} style={{ fontFamily: "inherit" }} />
              </div>
            )}
            {!client?.Email && (
              <p style={{ color: "var(--muted)", fontSize: ".875rem", margin: 0 }}>
                No email on file for {clientName || "this client"} — no notification will be sent.
              </p>
            )}
            {error && <div className="alert alert-error">{error}</div>}
            <div style={{ display: "flex", gap: ".75rem" }}>
              <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1 }}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={sending} style={{ flex: 2 }}>
                {sending ? "Sending…" : (client?.Email ? "Send & advance" : "Mark done")}
              </button>
            </div>
          </form>
        )}
    </Modal>
  );
}

